import { firebaseApp } from '../firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  Query,
  QuerySnapshot,
  QueryDocumentSnapshot,
  startAfter,
  writeBatch,
  serverTimestamp,
  increment,
  FieldValue,
  Timestamp,
  CollectionReference,
  DocumentData,
} from 'firebase/firestore';
import { Unsubscribe } from 'firebase/auth';

interface NotificationData extends DocumentData {
  type: string;
  content: string;
  url?: string;
  permanent?: boolean;
  important?: boolean;
  businessSiteId?: number | null;
  createdAt: Timestamp | string;
  readAt: Timestamp | string;
  deletedAt: Timestamp | string | null;
  isDeleted: boolean;
  isRead: boolean;
}

const QUERY_LIMIT = 50;
const NOTIFICATIONS = 'notifications';
const CREATED_AT = 'createdAt';
const NOTIFICATIONS_COUNT_BY_SITE_ID = 'notificationsCountBySiteId';

type ColProp = 'businesses' | 'users';

const userRef = (col: ColProp, userId: string) =>
  doc<NotificationData>(
    collection(
      getFirestore(firebaseApp),
      col,
    ) as CollectionReference<NotificationData>,
    userId,
  );

const notificationsRef = (col: ColProp, userId: string) =>
  collection(
    userRef(col, userId),
    NOTIFICATIONS,
  ) as CollectionReference<NotificationData>;

const getNotificationsQuery = (
  col: ColProp,
  userId: string,
): Query<NotificationData> => {
  const cur = new Date();
  const createdAt = new Date(
    cur.getFullYear(),
    cur.getMonth() - 1,
    cur.getDate(),
    0,
    0,
    0,
    0,
  );

  return query<NotificationData>(
    notificationsRef(col, userId),
    where('isDeleted', '==', false),
    where(CREATED_AT, '>=', createdAt),
    orderBy(CREATED_AT, 'desc'),
  );
};

export const getNotificationsCount = (
  col: ColProp,
  userId: string,
): Promise<number> => {
  const q = query(
    getNotificationsQuery(col, userId),
    where('isRead', '==', false),
  );
  return getDocs(q).then((doc) => doc.size);
};

export const listenNotificationsCount = (
  col: ColProp,
  userId: string,
  onChange: (count: number | null) => void,
  accessibleSiteIds: number[] | null = null,
): Unsubscribe => {
  return onSnapshot(userRef(col, userId), (snapshot) => {
    const data = snapshot.data();
    const bySiteId = data?.[NOTIFICATIONS_COUNT_BY_SITE_ID] as
      | Record<string, number>
      | undefined;

    if (!bySiteId) {
      // 後方互換: notificationsCountBySiteId が存在しない旧ドキュメントは notificationsCount にフォールバック
      onChange(data?.notificationsCount ?? null);
      return;
    }

    let count: number;
    if (accessibleSiteIds === null) {
      // 施設制限なし: 全キーを合計
      count = Object.values(bySiteId).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0,
      );
    } else {
      // 施設制限あり: 'null' キー（施設未指定）+ アクセス可能な施設 ID キーを合計
      const accessibleKeys = new Set<string>([
        'null',
        ...accessibleSiteIds.map(String),
      ]);
      count = Object.entries(bySiteId)
        .filter(([key]) => accessibleKeys.has(key))
        .reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
    }
    onChange(count);
  });
};

export const listenNotifications = (
  col: ColProp,
  userId: string,
  onChange: (docs: QuerySnapshot<DocumentData>) => void,
  l = QUERY_LIMIT,
): Unsubscribe => {
  const q = query(getNotificationsQuery(col, userId), limit(l));
  return onSnapshot(q, (snapshot) => onChange(snapshot));
};

export const getNotifications = (
  col: ColProp,
  userId: string,
  lastVisible: QueryDocumentSnapshot | null = null,
  l = QUERY_LIMIT,
): Promise<QueryDocumentSnapshot[]> => {
  let q = getNotificationsQuery(col, userId);
  if (lastVisible != null) {
    q = query(
      q,
      startAfter(lastVisible),
      where(CREATED_AT, '<=', lastVisible.data().createdAt),
      limit(l),
    );
  } else {
    q = query(q, limit(l));
  }
  return getDocs<NotificationData>(q).then((snapshot) => snapshot.docs);
};

export const readNotifications = (
  col: ColProp,
  userId: string,
  snaps: QueryDocumentSnapshot<NotificationData>[],
): Promise<void> => {
  const batch = writeBatch(getFirestore(firebaseApp));
  const readAt = serverTimestamp();

  const siteCountMap: Record<string, number> = {};
  snaps.forEach((snap) => {
    const docRef = doc(notificationsRef(col, userId), snap.id);
    batch.update(docRef, { readAt, isRead: true });
    if (!snap.data().isRead) {
      const siteKey = snap.data().businessSiteId?.toString() ?? 'null';
      siteCountMap[siteKey] = (siteCountMap[siteKey] || 0) + 1;
    }
  });

  const countUpdates: Record<string, FieldValue> = {};
  Object.entries(siteCountMap).forEach(([key, count]) => {
    countUpdates[key] = increment(-count);
  });

  batch.set(
    userRef(col, userId),
    { [NOTIFICATIONS_COUNT_BY_SITE_ID]: countUpdates },
    { merge: true },
  );

  return batch.commit();
};
